/**
 * Часы работы пятна — когда принимаются «Срочно» и «Ближайшая».
 *
 * Решение владельца 30.09.2026: «Срочно» и «Ближайшая» принимаются только в часы работы и не позже
 * чем за час до конца, а «Ко времени» — в любое время суток, на окна внутри часов. До этого
 * приложение предлагало «Ближайшую» в три часа ночи, сервер её принимал, и заказ висел в очереди до
 * утра, а клиент к открытию мог уже уехать.
 *
 * Всё считает сервер (`GET /v1/consumer/service-area` → `service_hours`); приложение хранит снимок
 * на черновике, как сетку окон, и само решает только одно — наступил ли уже момент, когда снимок
 * перестал быть правдой: приём закрылся, пока человек сидел на шаге выбора времени, или открылся,
 * пока он собирался утром.
 */
import type { DeliveryWindow } from './deliveryWindow';
import { applyDraftPatch, type OrderDraft } from './draft';

export interface ServiceHours {
  /** «09:00–23:00» или «круглосуточно». */
  label: string;
  /** «09:00–22:00» — когда принимаются «Срочно» и «Ближайшая»; «круглосуточно» тоже возможно. */
  asapLabel: string;
  /** Принимались ли «Срочно» и «Ближайшая» в момент проверки зоны. */
  asapAvailable: boolean;
  /** Пока принимаются: до какого момента сегодня (RFC3339). Нет у круглосуточного пятна. */
  asapAvailableUntil: string | null;
  /** Пока не принимаются: с какого момента снова (RFC3339). */
  asapAvailableFrom: string | null;
  /** Пока не принимаются: готовая фраза сервера — почему и что выбрать вместо. */
  asapUnavailableText: string | null;
}

function instant(rfc3339: string | null): Date | null {
  if (rfc3339 === null) return null;
  const parsed = new Date(rfc3339);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Можно ли прямо сейчас выбрать «Срочно» или «Ближайшую».
 *
 * `null` — бэкенд, который часов ещё не присылает: тогда как раньше, всё доступно, а последнее
 * слово остаётся за сервером. Снимок «открыто» перестаёт быть правдой в `asapAvailableUntil`,
 * снимок «закрыто» — в `asapAvailableFrom`; закрытый снимок без момента открытия не открывается
 * никогда, пока его не обновит новая проверка зоны.
 */
export function isAsapOpen(hours: ServiceHours | null, now: Date = new Date()): boolean {
  if (hours === null) return true;
  if (hours.asapAvailable) {
    const until = instant(hours.asapAvailableUntil);
    return until === null || now.getTime() < until.getTime();
  }
  const from = instant(hours.asapAvailableFrom);
  return from !== null && now.getTime() >= from.getTime();
}

/**
 * Что сказать человеку вместо «Срочно» и «Ближайшей», или `null`, когда они доступны.
 *
 * Пока снимок свежий — фраза сервера: она же придёт и в отказе на оформление, и в ней названо
 * ближайшее окно. Если приём закрылся уже после проверки зоны, сервер этого ещё не сказал, и фраза
 * собирается из часов приёма.
 */
export function asapClosedText(hours: ServiceHours | null, now: Date = new Date()): string | null {
  if (hours === null || isAsapOpen(hours, now)) return null;
  if (!hours.asapAvailable && hours.asapUnavailableText !== null) return hours.asapUnavailableText;
  return `«Срочно» и «Ближайшую» принимаем ${asapHoursPhrase(hours.asapLabel)}. Сейчас можно заказать только «Ко времени».`;
}

/** «с 09:00 до 22:00» из «09:00–22:00»; подпись другого вида — как есть, после «в часы». */
export function asapHoursPhrase(label: string): string {
  const [from, to] = label.split('–');
  if (from !== undefined && to !== undefined && from.length > 0 && to.length > 0) {
    return `с ${from} до ${to}`;
  }
  return `в часы ${label}`;
}

/**
 * Кладёт часы на черновик и, если «Срочно» и «Ближайшая» сейчас не принимаются, сразу переводит
 * выбор на «Ко времени».
 *
 * Через `applyDraftPatch`, а не подменой поля: смена скорости меняет сумму (надбавку за
 * срочность), и ключ идемпотентности у такого заказа обязан быть новым.
 */
export function withServiceHours(
  draft: OrderDraft,
  hours: ServiceHours | null,
  now: Date = new Date(),
): OrderDraft {
  const next = { ...draft, serviceHours: hours };
  if (next.timeMode === 'scheduled' || isAsapOpen(hours, now)) return next;
  return applyDraftPatch(next, { timeMode: 'scheduled' });
}

/**
 * Свежий снимок пятна для черновика, поднятого из хранилища: часы, сетка окон, время выезда — и
 * больше ничего.
 *
 * Черновик живёт сутки, и снимок в нём стареет: утром вчерашнее «принимаем до 22:00» гасит
 * «Срочно», хотя приём уже идёт, а окна в нём прошли. Скорость, шаг и ключ идемпотентности здесь не
 * трогаются: за поднятым черновиком может стоять начатая оплата (возврат из банка, вкладка с СБП,
 * перезагруженная посреди ожидания), и новый ключ оторвал бы от неё заказ. Если на сводке стоит
 * «Ближайшая», а приём уже закрыт, последнее слово скажет сервер: отказ `asap_closed` вернёт
 * человека на шаг времени с его фразой.
 */
export function withFreshSnapshot(
  draft: OrderDraft,
  hours: ServiceHours | null,
  windows: DeliveryWindow[],
  dispatchLeadMinutes: number | null,
): OrderDraft {
  return { ...draft, serviceHours: hours, deliveryWindows: windows, dispatchLeadMinutes };
}

/**
 * Снимок «открыто», который часы уже опровергли, на поднятом черновике — не знание, а догадка.
 *
 * Записанное вчера днём «принимаем до 22:00» утром гасило бы «Срочно» и сразу переводило выбор на
 * «Ко времени» — ещё до того, как придёт свежий снимок. Поэтому при подъёме черновика такой снимок
 * забывается: без часов приложение ведёт себя как раньше, а через мгновение их приносит
 * перепроверка пятна. Пока человек сидит на шаге, тот же снимок, наоборот, честно закрывается в
 * свой момент — эта функция для этого не зовётся.
 */
export function withoutStaleHours(draft: OrderDraft, now: Date = new Date()): OrderDraft {
  const hours = draft.serviceHours;
  if (hours === null || !hours.asapAvailable) return draft;
  const until = instant(hours.asapAvailableUntil);
  return until !== null && now.getTime() >= until.getTime()
    ? { ...draft, serviceHours: null }
    : draft;
}
